
import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useUserReport } from '@/hooks/useUserReport';

const SavingsBreakdownSection = () => {
  const { data: report } = useUserReport();

  const formatGBP = (value: number | undefined) => {
    if (value === undefined) return 'N/A';
    return `£${value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercentage = (value: number | undefined) => {
    if (value === undefined) return 'N/A';
    return `${value.toFixed(2)}%`;
  };

  return (
    <section className="w-full bg-white py-8">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-2xl font-bold text-[#1F2937] mb-6">
            Savings Breakdown
          </h2>
          
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-[#F9FAFB]">
                <TableRow>
                  <TableHead className="w-[527px]"></TableHead>
                  <TableHead className="w-[216px] text-right font-semibold text-[#1F2937]">Gas</TableHead>
                  <TableHead className="w-[259px] text-right font-semibold text-[#1F2937]">Electricity</TableHead>
                  <TableHead className="w-[196px] text-right font-semibold text-[#1F2937]">Water</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="text-[#4B5563]">Average Yearly Cost for your Profile</TableCell>
                  <TableCell className="text-right text-[#1F2937]">{formatGBP(report?.gas_avg_cost)}</TableCell>
                  <TableCell className="text-right text-[#1F2937]">{formatGBP(report?.elec_avg_cost)}</TableCell>
                  <TableCell className="text-right text-[#1F2937]">{formatGBP(report?.water_avg_cost)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="text-[#4B5563]">Your profile vs the average</TableCell>
                  <TableCell className="text-right text-[#EF4444]">{report?.gas_diff_your_cost || 0}%</TableCell>
                  <TableCell className="text-right text-[#EF4444]">{report?.elec_diff_your_cost || 0}%</TableCell>
                  <TableCell className="text-right text-[#EF4444]">{report?.water_diff_your_cost || 0}%</TableCell>
                </TableRow>
                <TableRow className="bg-[#F9FAFB]">
                  <TableCell colSpan={4} className="font-semibold text-[#374151]">Consumption</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="text-[#4B5563]">Your Annual Energy Cost</TableCell>
                  <TableCell className="text-right text-[#1F2937]">{formatGBP(report?.gas_curr_cost)}</TableCell>
                  <TableCell className="text-right text-[#1F2937]">{formatGBP(report?.elec_curr_cost)}</TableCell>
                  <TableCell className="text-right text-[#1F2937]">{formatGBP(report?.water_curr_cost)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="text-[#4B5563]">Potential Savings from KOSI Interventions</TableCell>
                  <TableCell className="text-right text-[#059669]">{formatPercentage(report?.gas_use_sav_pot || 0)}</TableCell>
                  <TableCell className="text-right text-[#059669]">{formatPercentage(report?.elec_use_sav_pot || 0)}</TableCell>
                  <TableCell className="text-right text-[#059669]">{formatPercentage(report?.water_use_sav_pot || 0)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="text-[#4B5563]">Total Savings from reduced Consumption</TableCell>
                  <TableCell className="text-right text-[#059669]">{formatGBP(report?.gas_use_tot_sav)}</TableCell>
                  <TableCell className="text-right text-[#059669]">{formatGBP(report?.elec_use_tot_sav)}</TableCell>
                  <TableCell className="text-right text-[#059669]">{formatGBP(report?.water_use_tot_sav)}</TableCell>
                </TableRow>
                <TableRow className="bg-[#F9FAFB]">
                  <TableCell colSpan={4} className="font-semibold text-[#374151]">Tariff Switching</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="text-[#4B5563]">Tariff Savings Potential</TableCell>
                  <TableCell className="text-right text-[#10B981]">{formatPercentage(report?.gas_price_sav_pot || 0)}</TableCell>
                  <TableCell className="text-right text-[#10B981]">{formatPercentage(report?.elec_price_sav_pot || 0)}</TableCell>
                  <TableCell className="text-right text-[#10B981]">-</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="text-[#4B5563]">Tariff Savings</TableCell>
                  <TableCell className="text-right text-[#059669]">{formatGBP(report?.gas_price_tot_sav)}</TableCell>
                  <TableCell className="text-right text-[#059669]">{formatGBP(report?.elec_price_tot_sav)}</TableCell>
                  <TableCell className="text-right text-[#059669]">-</TableCell>
                </TableRow>
                <TableRow className="bg-[#F9FAFB]">
                  <TableCell colSpan={4} className="font-semibold text-[#374151]">Combined</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="text-[#4B5563]">Savings Potential</TableCell>
                  <TableCell className="text-right text-[#10B981]">{formatGBP(report?.gas_comb_sav_pot)}</TableCell>
                  <TableCell className="text-right text-[#10B981]">{formatGBP(report?.elec_comb_sav_pot)}</TableCell>
                  <TableCell className="text-right text-[#10B981]">{formatGBP(report?.water_comb_sav_pot)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="text-[#4B5563]">Impact</TableCell>
                  <TableCell className="text-right text-[#10B981]">{report?.gas_comb_sav_pot_perc || 0}%</TableCell>
                  <TableCell className="text-right text-[#10B981]">{report?.elec_comb_sav_pot_perc || 0}%</TableCell>
                  <TableCell className="text-right text-[#10B981]">{report?.water_comb_sav_pot_perc || 0}%</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SavingsBreakdownSection;
